import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BookOpen, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/feed")({
  head: () => pageHead({
    path: "/feed",
    title: "Activity feed — Book Swap",
    description: "See the newest books listed by neighbors you follow and around your area, updated as members add to their shelves.",
  }),
  component: Feed,
});

type Item = { id: string; title: string; author: string; cover_url: string | null; created_at: string; user_id: string; ownerName?: string };

function Feed() {
  const { user } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: f } = await supabase.from("follows").select("following_id").eq("follower_id", user.id);
      const ids = (f ?? []).map((x: any) => x.following_id);
      if (ids.length === 0) { setEmpty(true); return; }
      const { data: bs } = await supabase
        .from("books").select("id,title,author,cover_url,created_at,user_id")
        .in("user_id", ids).order("created_at", { ascending: false }).limit(40);
      const list = (bs as Item[]) ?? [];
      const { data: profs } = await supabase.from("public_profiles").select("id,full_name").in("id", ids);
      const map = new Map((profs ?? []).map((p: any) => [p.id, p.full_name]));
      setItems(list.map((b) => ({ ...b, ownerName: map.get(b.user_id) ?? "A neighbor" })));
    })();
  }, [user]);

  if (!user) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center">Sign in to see your feed.</p></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="flex items-center gap-2 text-3xl font-bold"><Users className="h-7 w-7 text-primary" /> Activity feed</h1>
        <p className="mt-1 text-muted-foreground">Newest books from readers you follow.</p>

        {empty ? (
          <div className="mt-10 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <p className="font-semibold">You're not following anyone yet.</p>
            <p className="mt-1 text-sm text-muted-foreground">Open any book and tap the lister's name to follow them.</p>
          </div>
        ) : items.length === 0 ? <p className="mt-8 text-muted-foreground">No new books from your circle yet.</p> : (
          <ul className="mt-6 space-y-4">
            {items.map((b) => (
              <li key={b.id} className="flex gap-4 rounded-2xl bg-card p-4 shadow-[var(--shadow-card)]">
                <Link to="/book/$id" params={{ id: b.id }} className="block h-20 w-14 shrink-0 overflow-hidden rounded bg-primary-soft">
                  {b.cover_url ? <img src={b.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><BookOpen className="h-6 w-6 text-primary/40" /></div>}
                </Link>
                <div className="flex-1">
                  <Link to="/u/$id" params={{ id: b.user_id }} className="text-sm font-semibold text-primary hover:underline">{b.ownerName}</Link>
                  <p className="text-xs text-muted-foreground">listed a book</p>
                  <Link to="/book/$id" params={{ id: b.id }} className="mt-1 block font-semibold hover:underline">{b.title}</Link>
                  <p className="text-sm text-muted-foreground">by {b.author}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
